import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import map from 'lodash/map';
import toPairs from 'lodash/toPairs';
import { isNull } from 'lodash';
import clsx from 'clsx';
import { allowOnlyNumber } from '@/utils/functions';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { showNotification } from '@/utils/showNotification';
import { useGetCountries } from '@/api/queryHooks/useKycController';
import { Button } from '../../buttons';
import { Input } from '../Input';
import { GooglePlaces, SelectedAddress } from '../GooglePlaces';
import styles from './Location.module.css';
import { SelectListItem } from '../SelectList';
import { Select } from '../Select';

// Local schema — the global profileLocationSchema was removed with RNB-187.
// This form is presentational: it validates and hands the result to `onSubmit`;
// persisting the address is the caller's responsibility.
export const locationFormSchema = z
  .object({
    fullAddress: z.string().min(1),
    countryIso: z.string(),
    country: z.string().min(1),
    state: z.string().min(1),
    town: z.string().min(1),
    street: z.string().min(1),
    postcode: z.string().min(1),
    unitNumber: z.string(),
    buildingNumber: z.string(),
  })
  .refine((data) => Boolean(data.unitNumber || data.buildingNumber), {
    message: 'Unit number or building number is required.',
  });

export interface LocationFormData extends SelectedAddress {
  fullAddress: string;
}

interface LocationProps {
  isPopup?: boolean;
  isReadOnly?: boolean;
  isLoading?: boolean;
  buttonText?: string;
  defaultValues?: Partial<LocationFormData>;
  onSubmit: (data: LocationFormData) => void;
}

type AddressKey = keyof LocationFormData;

const emptyValues: LocationFormData = {
  fullAddress: '',
  countryIso: '',
  country: '',
  state: '',
  town: '',
  street: '',
  postcode: '',
  unitNumber: '',
  buildingNumber: '',
};

export const Location = ({
  isPopup,
  isReadOnly = false,
  isLoading = false,
  buttonText = 'Save Address',
  defaultValues,
  onSubmit,
}: LocationProps) => {
  const { isDesktop } = useMediaQuery();
  const { data: countries } = useGetCountries();

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: { isValid },
    watch,
    reset,
  } = useForm<LocationFormData>({
    resolver: zodResolver(locationFormSchema),
    defaultValues: { ...emptyValues, ...defaultValues },
  });

  const watchedCountry = watch('country');

  function setAddressValues(addressObj: SelectedAddress | LocationFormData) {
    const addressPairs = toPairs(addressObj);
    map(addressPairs, ([key, value]) => {
      setValue(key as AddressKey, value);
    });
    trigger();
  }

  function onGooglePlacesSuccess(selectedAddress: SelectedAddress) {
    setAddressValues(selectedAddress);
  }

  function onGooglePlacesError(error: string) {
    showNotification(error);
  }

  const isFieldDisabled = isNull(watchedCountry) || watchedCountry === '';

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="country"
        control={control}
        render={({ field: { name, value, onBlur } }) => (
          <Select
            isReadOnly={isReadOnly}
            className={styles.select}
            name={name}
            onBlur={onBlur}
            label="Country"
            placeholder="Please select your country"
            inputValue={countries?.find((c) => c.iso2 === value)?.name || ''}
            onSelectionChange={(e) => {
              const country = countries?.[Number(e)];
              if (country) {
                reset({
                  ...emptyValues,
                  country: country.iso2,
                });
              }
            }}
          >
            {(countries ?? []).map((item, index) => (
              <SelectListItem key={index} textValue={item?.name}>
                {item?.name}
              </SelectListItem>
            ))}
          </Select>
        )}
      />
      <Controller
        name="fullAddress"
        control={control}
        render={({ field }) => (
          <GooglePlaces
            {...field}
            availableCountry={watchedCountry}
            className={styles.fullWidthElement}
            size={isDesktop ? 'large' : 'medium'}
            placeholder="Full Address"
            label="Full Address"
            onSuccess={onGooglePlacesSuccess}
            onError={onGooglePlacesError}
            isReadOnly={isReadOnly}
            isDisabled={isFieldDisabled}
          />
        )}
      />

      <Controller
        name="state"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            isReadOnly={isReadOnly}
            className={styles.halfWidthElement}
            size={isDesktop ? 'large' : 'medium'}
            placeholder="State"
            label="State"
            isDisabled={isFieldDisabled}
          />
        )}
      />
      <Controller
        name="town"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            isReadOnly={isReadOnly}
            className={styles.halfWidthElement}
            size={isDesktop ? 'large' : 'medium'}
            placeholder="Suburb"
            label="Suburb"
            isDisabled={isFieldDisabled}
          />
        )}
      />
      <Controller
        name="street"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            isReadOnly={isReadOnly}
            className={styles.halfWidthElement}
            size={isDesktop ? 'large' : 'medium'}
            placeholder="Street"
            label="Street"
            isDisabled={isFieldDisabled}
          />
        )}
      />
      <Controller
        name="postcode"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Input
            isReadOnly={isReadOnly}
            onChange={(text) => onChange(allowOnlyNumber(text))}
            value={value}
            className={styles.halfWidthElement}
            size={isDesktop ? 'large' : 'medium'}
            placeholder="Postcode"
            label="Postcode"
            isDisabled={isFieldDisabled}
          />
        )}
      />
      <Controller
        name="unitNumber"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            isReadOnly={isReadOnly}
            className={styles.halfWidthElement}
            size={isDesktop ? 'large' : 'medium'}
            placeholder="Unit Number"
            label="Unit Number"
            isDisabled={isFieldDisabled}
          />
        )}
      />
      <Controller
        name="buildingNumber"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            isReadOnly={isReadOnly}
            className={styles.halfWidthElement}
            size={isDesktop ? 'large' : 'medium'}
            placeholder="Building Number"
            label="Building Number"
            isDisabled={isFieldDisabled}
          />
        )}
      />
      <Button
        className={clsx(styles.fullWidthElement, styles.button)}
        size={isDesktop ? 'large' : 'medium'}
        type="submit"
        isDisabled={!isValid || isReadOnly || isFieldDisabled}
        loading={isLoading}
        fluid={isPopup && !isDesktop}
      >
        {buttonText}
      </Button>
    </form>
  );
};
